import React, { useState } from "react";
import { useLang } from "../context/LanguageContext";
import api from "../lib/api";
import { toast } from "sonner";
import { Send } from "lucide-react";

export default function BookingForm() {
  const { t } = useLang();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    project_type: t.booking.project_types[0],
    location: "",
    project_date: "",
    budget: t.booking.budgets[0],
    deliverables: [],
    urgency: t.booking.urgencies[0],
    references: "",
    message: "",
  });
  const [sending, setSending] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleDeliverable = (d) => {
    setForm((f) => ({
      ...f,
      deliverables: f.deliverables.includes(d)
        ? f.deliverables.filter((x) => x !== d)
        : [...f.deliverables, d],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.project_type) {
      toast.error("Please fill in name, email and project type");
      return;
    }
    setSending(true);
    try {
      await api.post("/quotes", form);
      toast.success(t.booking.success);
      setForm({
        name: "", email: "", phone: "", company: "",
        project_type: t.booking.project_types[0],
        location: "", project_date: "",
        budget: t.booking.budgets[0],
        deliverables: [],
        urgency: t.booking.urgencies[0],
        references: "", message: "",
      });
    } catch {
      toast.error(t.booking.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="book"
      data-testid="booking-section"
      className="relative bg-[#050505] py-24 md:py-32 px-6 md:px-12 border-t border-white/5"
    >
      <div className="max-w-[1600px] mx-auto grid grid-cols-12 gap-8">
        <div className="col-span-12 md:col-span-4 lg:col-span-4">
          <p className="font-mono-tight text-zinc-500 mb-6">{t.booking.label}</p>
          <h2 className="text-5xl md:text-7xl font-display tracking-tighter text-white mb-8" data-testid="booking-title">
            {t.booking.title}
          </h2>
          <p className="text-zinc-400 text-base leading-relaxed max-w-md" data-testid="booking-sub">
            {t.booking.sub}
          </p>
        </div>

        <form
          onSubmit={submit}
          className="col-span-12 md:col-span-8 lg:col-span-7 lg:col-start-6 mt-8 md:mt-0"
          data-testid="booking-form"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
            <div className="field">
              <input
                type="text"
                placeholder=" "
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                required
                data-testid="booking-name-input"
              />
              <label>{t.booking.fields.name}</label>
            </div>
            <div className="field">
              <input
                type="email"
                placeholder=" "
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                required
                data-testid="booking-email-input"
              />
              <label>{t.booking.fields.email}</label>
            </div>
            <div className="field">
              <input
                type="tel"
                placeholder=" "
                value={form.phone}
                onChange={(e) => update("phone", e.target.value)}
                data-testid="booking-phone-input"
              />
              <label>{t.booking.fields.phone}</label>
            </div>
            <div className="field">
              <input
                type="text"
                placeholder=" "
                value={form.company}
                onChange={(e) => update("company", e.target.value)}
                data-testid="booking-company-input"
              />
              <label>{t.booking.fields.company}</label>
            </div>

            <div className="field has-value">
              <select
                value={form.project_type}
                onChange={(e) => update("project_type", e.target.value)}
                data-testid="booking-project-type-select"
              >
                {t.booking.project_types.map((p) => (
                  <option key={p} value={p} className="bg-black">{p}</option>
                ))}
              </select>
              <label>{t.booking.fields.project_type}</label>
            </div>
            <div className="field">
              <input
                type="text"
                placeholder=" "
                value={form.location}
                onChange={(e) => update("location", e.target.value)}
                data-testid="booking-location-input"
              />
              <label>{t.booking.fields.location}</label>
            </div>
            <div className="field has-value">
              <input
                type="date"
                placeholder=" "
                value={form.project_date}
                onChange={(e) => update("project_date", e.target.value)}
                data-testid="booking-date-input"
                style={{ colorScheme: "dark" }}
              />
              <label>{t.booking.fields.project_date}</label>
            </div>
            <div className="field has-value">
              <select
                value={form.budget}
                onChange={(e) => update("budget", e.target.value)}
                data-testid="booking-budget-select"
              >
                {t.booking.budgets.map((b) => (
                  <option key={b} value={b} className="bg-black">{b}</option>
                ))}
              </select>
              <label>{t.booking.fields.budget}</label>
            </div>

            <div className="field has-value">
              <select
                value={form.urgency}
                onChange={(e) => update("urgency", e.target.value)}
                data-testid="booking-urgency-select"
              >
                {t.booking.urgencies.map((u) => (
                  <option key={u} value={u} className="bg-black">{u}</option>
                ))}
              </select>
              <label>{t.booking.fields.urgency}</label>
            </div>
            <div className="field">
              <input
                type="text"
                placeholder=" "
                value={form.references}
                onChange={(e) => update("references", e.target.value)}
                data-testid="booking-references-input"
              />
              <label>{t.booking.fields.references}</label>
            </div>
          </div>

          {/* Deliverables */}
          <div className="mb-8 mt-2">
            <p className="font-mono-tight text-zinc-500 mb-4">{t.booking.fields.deliverables}</p>
            <div className="flex flex-wrap gap-2">
              {t.booking.deliverables.map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => toggleDeliverable(d)}
                  data-testid={`booking-deliv-${d.toLowerCase().replace(/[^a-z0-9]/g, "-")}-btn`}
                  className={`font-mono-tight px-4 py-2 border transition-all ${
                    form.deliverables.includes(d)
                      ? "bg-white text-black border-white"
                      : "border-white/15 text-zinc-400 hover:border-white"
                  }`}
                >
                  {d}
                </button>
              ))}
            </div>
          </div>

          <div className="field">
            <textarea
              placeholder=" "
              value={form.message}
              onChange={(e) => update("message", e.target.value)}
              data-testid="booking-message-input"
            />
            <label>{t.booking.fields.message}</label>
          </div>

          <button
            type="submit"
            disabled={sending}
            className="btn-primary"
            data-testid="booking-submit-btn"
          >
            {sending ? t.booking.sending : t.booking.submit}
            <Send size={16} />
          </button>
        </form>
      </div>
    </section>
  );
}
